import { getName } from "@rentiohq/shared-frontend/dist/redux/contact/contact.utils";
import { IApplication } from "@rentiohq/shared-frontend/dist/types/application.types";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { Modal, OptionListShared } from "@rentiohq/web-shared/dist/components";
import React from "react";
import {
  getDefaultCandidacyDeadline,
  toCandidacyDeadlinePayload,
} from "utils/candidacyDeadline.utils";
import { Rating } from "../../../../../components/Rating";
import * as S from "../VisitMode.styled";
import { CandidacyDeadlineInput } from "./CandidacyDeadlineInput";

interface IProps {
  /** Candidates who may be invited: attended, not invited yet. */
  eligible: IApplication[];
  /** No-shows, listed but never eligible for an invite. */
  skipped: IApplication[];
  /** The opening line, naming the day (or the visitors without a time) being
   * handled, said out loud because the action never reaches beyond the open
   * tab. */
  intro: string;
  onClose: () => void;
  /** The deadline is 'YYYY-MM-DD', or null for none, one for everybody. */
  onConfirm: (
    applicationIds: string[],
    candidacyDeadline: string | null,
  ) => void;
}

/**
 * The bulk invite is a choice, not a send (DEV-3227).
 *
 * This is also the only place the scores sit next to each other. In the list
 * behind it you cannot see the first person's score any more once you are at
 * the fourth, and deciding who to invite is exactly a comparison.
 *
 * Everyone starts ticked, because inviting everybody who turned up is the
 * normal case; clearing a tick is how you leave somebody out.
 */
export const InviteSelectionModal = (props: IProps) => {
  const { eligible, skipped, intro, onClose, onConfirm } = props;

  const [selected, setSelected] = React.useState<string[]>(() =>
    eligible.map(application => application.id),
  );
  const [candidacyDeadline, setCandidacyDeadline] = React.useState<Date | null>(
    () => getDefaultCandidacyDeadline(),
  );

  const renderLabel = (application: IApplication) => {
    const contact = application.contacts?.[0];
    return (
      <S.SelectionRow>
        <S.SelectionLeft>{contact ? getName(contact) : ""}</S.SelectionLeft>
        {application.rating != null && (
          <Rating value={application.rating} size={14} />
        )}
      </S.SelectionRow>
    );
  };

  return (
    <Modal
      width="small"
      heading={getLocalizedText("visit_mode.invite.heading")}
      hasDismiss={true}
      onClose={onClose}
      actions={[
        {
          content: getLocalizedText("visit_mode.invite.confirm", {
            count: `${selected.length}`,
          }),
          onClick: () =>
            onConfirm(selected, toCandidacyDeadlinePayload(candidacyDeadline)),
          appearance: "primary",
          isDisabled: selected.length === 0,
        },
      ]}
    >
      <S.SelectionIntro>{intro}</S.SelectionIntro>

      <OptionListShared
        id="visit-mode-invite-select"
        variant="simple"
        multiple={true}
        value={selected}
        options={eligible.map(application => ({
          label: renderLabel(application),
          value: application.id,
          id: application.id,
        }))}
        onChange={(selection: string[]) => setSelected(selection)}
      />

      <CandidacyDeadlineInput
        value={candidacyDeadline}
        onChange={setCandidacyDeadline}
      />

      {skipped.length > 0 && (
        <>
          <S.SkippedTitle>
            {getLocalizedText("visit_mode.invite.skipped")}
          </S.SkippedTitle>
          {skipped.map(application => {
            const contact = application.contacts?.[0];
            return (
              <S.SkippedRow key={application.id}>
                <span>{contact ? getName(contact) : ""}</span>
                <span>{getLocalizedText("visit_mode.card.no_show")}</span>
              </S.SkippedRow>
            );
          })}
        </>
      )}
    </Modal>
  );
};
